import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
    ViewEncapsulation,
} from "@angular/core";

interface DynamicDashboardReport {
    name: string;
    description: string;
    code: string;
    type: string;
    restrictionType: "D" | "H" | null;
    chartType: string;
    valueType: string;
    layoutColumns?: number;
    usesPeriodFilter?: boolean;
    usesDashboardMonthYearFilter?: boolean;
    filters: DynamicDashboardFilter[];
    data: any;
}

interface DynamicDashboardFilter {
    key: string;
    label: string;
    type: "MULTI_SELECT" | "SELECT" | "TEXT" | "NUMBER";
    defaultValue?: any;
    options?: Array<{ label: string; value: any }>;
}

interface DynamicDashboardListRow {
    group?: string;
    label: string;
    value: any;
    detail?: string;
}

interface DynamicDashboardTableColumn {
    key: string;
    label: string;
    type?: string;
}

interface DynamicDashboardFilterBadge {
    label: string;
    value: string;
}

interface DynamicDashboardFiltersChange {
    filters: Record<string, any>;
    reportCodes: string[];
}

interface DynamicDashboardChartTypeChange {
    reportCode: string;
    chartType: string;
}

interface DynamicDashboardChartTypeOption {
    chartType: string;
    label: string;
}

@Component({
    selector: "app-dashboard-dynamic-reports",
    templateUrl: "./dashboard-dynamic-reports.component.html",
    styleUrls: ["./dashboard-dynamic-reports.component.scss"],
    encapsulation: ViewEncapsulation.None,
})
export class DashboardDynamicReportsComponent
    implements OnChanges, AfterViewInit, OnDestroy
{
    @Input() reports: DynamicDashboardReport[] = [];
    @Input() filterData: any;
    @Input() loadingReportCodes: string[] = [];
    @Input() loadedReportCodes: string[] = [];
    @Input() expandCommand: { expanded: boolean; version: number } | null = null;
    @Input() filtersEnabled = true;
    @Output() reportsVisible = new EventEmitter<string[]>();
    @Output() filtersChange = new EventEmitter<DynamicDashboardFiltersChange>();
    @Output() chartTypeChange = new EventEmitter<DynamicDashboardChartTypeChange>();
    @ViewChildren("reportGridItem") reportGridItems!: QueryList<ElementRef<HTMLElement>>;

    chartOptionsByCode: Record<string, any> = {};
    expandedByCode: Record<string, boolean> = {};
    filtersVisibleByCode: Record<string, boolean> = {};
    filterValuesByCode: Record<string, Record<string, any>> = {};
    viewModeByCode: Record<string, "chart" | "list"> = {};
    private previousReportRefsByCode: Record<string, DynamicDashboardReport> = {};
    private lastExpandCommandVersion: number | null = null;
    private reportVisibilityObserver?: IntersectionObserver;
    private reportGridItemsChangesSubscription: any;
    private reportObservationScheduled = false;
    private readonly emittedVisibleReportCodes = new Set<string>();
    private readonly tableValueDelimiter = "__CW_TABLE__";
    private readonly chartTypeOptions: Record<string, DynamicDashboardChartTypeOption> = {
        COLUMN_PERCENT_GROUP: {
            chartType: "COLUMN_PERCENT_GROUP",
            label: "Colunas",
        },
        RADIAL_BAR: {
            chartType: "RADIAL_BAR",
            label: "Radial",
        },
        BAR_HORIZONTAL_PERCENT: {
            chartType: "BAR_HORIZONTAL_PERCENT",
            label: "Barras",
        },
        LINE_PERCENT: {
            chartType: "LINE_PERCENT",
            label: "Linha",
        },
        BAR_HORIZONTAL_VALUE: {
            chartType: "BAR_HORIZONTAL_VALUE",
            label: "Barras",
        },
        COLUMN_VALUE: {
            chartType: "COLUMN_VALUE",
            label: "Colunas",
        },
        COLUMN_MULTIPLE_VALUE: {
            chartType: "COLUMN_MULTIPLE_VALUE",
            label: "Colunas",
        },
        LINE_VALUE: {
            chartType: "LINE_VALUE",
            label: "Linha",
        },
        LINE_MULTI_SERIES_VALUE: {
            chartType: "LINE_MULTI_SERIES_VALUE",
            label: "Linha",
        },
        POLAR_AREA: {
            chartType: "POLAR_AREA",
            label: "Pizza",
        },
    };
    private readonly compatibleChartTypeGroups: string[][] = [
        [
            "COLUMN_PERCENT_GROUP",
            "RADIAL_BAR",
            "BAR_HORIZONTAL_PERCENT",
            "LINE_PERCENT",
        ],
        [
            "COLUMN_VALUE",
            "BAR_HORIZONTAL_VALUE",
            "LINE_VALUE",
            "POLAR_AREA",
        ],
        ["COLUMN_MULTIPLE_VALUE", "LINE_MULTI_SERIES_VALUE"],
    ];
    private readonly yearOnlyReportCodes = new Set<string>([
        "dashboards-schedule-absenteeism-evolution",
        "dashboards-schedule-absenteeism-schedule",
        "dashboards-order-production-evolution",
        "dashboards-order-production-year-comparison",
        "dashboards-order-production-group-evolution",
        "dashboards-financial-cash-flow-evolution",
        "dashboards-managerial-production-year-comparison",
        "dashboards-managerial-production-group-evolution",
        "dashboards-managerial-new-patients-yearly",
        "dashboards-managerial-attended-patients-yearly",
    ]);
    private readonly dateIndependentReportCodes = new Set<string>([
        "dashboards-billing-alerts",
        "dashboards-billing-unbilled-guides-insurance",
        "dashboards-billing-average-receipt-time-insurance",
        "dashboards-billing-receivable-invoices-insurance",
        "dashboards-billing-overdue-invoices-insurance",
        "dashboards-financial-cash-flow-next-30-days",
        "dashboards-financial-payment-status",
        "dashboards-financial-receipt-status",
        "dashboards-financial-average-receipt-time-insurance",
    ]);
    private readonly ownCurrentMonthPeriodReportCodes = new Set<string>([]);
    private readonly ownCurrentDayReportCodes = new Set<string>([
        "dashboards-order-patient-stay-average",
    ]);
    private readonly requiredFiltersByCode: Record<
        string,
        DynamicDashboardFilter[]
    > = {
        "dashboards-schedule-absenteeism-professional": [
            this.createLimitRowsFilter(),
        ],
        "dashboards-order-production-insurance": [
            this.createLimitRowsFilter(),
        ],
        "dashboards-order-production-group": [
            this.createLimitRowsFilter(),
        ],
        "dashboards-order-production-performer": [
            this.createLimitRowsFilter(),
        ],
    };

    private readonly statusColorMap: Record<string, string> = {
        N: "#B0BEC5",
        Q: "#FBC02D",
        C: "#2E7D32",
        B: "#C62828",
        W: "#D32F2F",
        Z: "#B71C1C",
        V: "#00ACC1",
        T: "#EF6C00",
        F: "#8E0000",
        L: "#6A1B9A",
        A: "#1B5E20",
        X: "#004D40",
        E: "#1565C0",
        P: "#42A5F5",
        U: "#7B1FA2",
        R: "#43A047",
        I: "#757575",
        S: "#1976D2",
    };

    private readonly chartColorPalette: string[] = [
        "#00897B",
        "#3949AB",
        "#F9A825",
        "#E53935",
        "#7E57C2",
        "#039BE5",
        "#43A047",
        "#FB8C00",
        "#D81B60",
        "#546E7A",
        "#00ACC1",
        "#5E35B1",
        "#C0CA33",
        "#F4511E",
        "#1E88E5",
        "#8E24AA",
        "#6D4C41",
        "#26A69A",
        "#EF5350",
        "#9CCC65",
        "#FFA726",
        "#42A5F5",
        "#AB47BC",
        "#78909C",
        "#D4E157",
        "#FF7043",
        "#26C6DA",
        "#66BB6A",
        "#EC407A",
        "#5C6BC0",
        "#8D6E63",
        "#FFCA28",
        "#29B6F6",
        "#7CB342",
        "#C2185B",
        "#00695C",
        "#283593",
        "#EF6C00",
        "#AD1457",
        "#0277BD",
        "#558B2F",
        "#6A1B9A",
        "#BF360C",
        "#00838F",
        "#9E9D24",
        "#4527A0",
        "#C62828",
        "#37474F",
    ];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["reports"]) {
            this.emittedVisibleReportCodes.clear();
            this.initializeFilterValues();
            this.buildChartOptions();
            this.scheduleReportObservation();
        }

        if (changes["expandCommand"]) {
            this.applyExpandCommand();
        }

        if (changes["filtersEnabled"] && !this.filtersEnabled) {
            this.filtersVisibleByCode = {};
        }
    }

    ngAfterViewInit(): void {
        this.reportGridItemsChangesSubscription =
            this.reportGridItems.changes.subscribe(() =>
                this.scheduleReportObservation(),
            );
        this.scheduleReportObservation();
    }

    ngOnDestroy(): void {
        this.reportVisibilityObserver?.disconnect();
        this.reportGridItemsChangesSubscription?.unsubscribe?.();
    }

    get visibleReports(): DynamicDashboardReport[] {
        return (this.reports || []).filter((report) =>
            this.isReportVisible(report),
        );
    }

    trackReportByCode(_index: number, report: DynamicDashboardReport): string {
        return report.code;
    }

    isReportDisabled(report: DynamicDashboardReport): boolean {
        return report.restrictionType === "D";
    }

    isReportLoading(report: DynamicDashboardReport): boolean {
        return (this.loadingReportCodes || []).includes(report.code);
    }

    isReportLoaded(report: DynamicDashboardReport): boolean {
        return (this.loadedReportCodes || []).includes(report.code);
    }

    private scheduleReportObservation(): void {
        if (this.reportObservationScheduled) {
            return;
        }

        this.reportObservationScheduled = true;

        setTimeout(() => {
            this.reportObservationScheduled = false;
            this.observeReportCards();
        });
    }

    private observeReportCards(): void {
        const reportElements = this.reportGridItems?.toArray() || [];

        this.reportVisibilityObserver?.disconnect();

        if (!reportElements.length) {
            return;
        }

        if (typeof IntersectionObserver === "undefined") {
            this.emitVisibleReportCodes(
                this.visibleReports.map((report) => report.code),
            );
            return;
        }

        this.reportVisibilityObserver = new IntersectionObserver(
            (entries) => {
                const visibleCodes = entries
                    .filter((entry) => entry.isIntersecting)
                    .map((entry) =>
                        (entry.target as HTMLElement).getAttribute(
                            "data-report-code",
                        ),
                    )
                    .filter((code): code is string => !!code);

                this.emitVisibleReportCodes(visibleCodes);
            },
            {
                root: null,
                rootMargin: "360px 0px",
                threshold: 0.01,
            },
        );

        reportElements.forEach((item) => {
            this.reportVisibilityObserver?.observe(item.nativeElement);
        });

        this.emitInitiallyVisibleReportCodes(reportElements);
    }

    private emitInitiallyVisibleReportCodes(
        reportElements: ElementRef<HTMLElement>[],
    ): void {
        const viewportHeight =
            window.innerHeight || document.documentElement.clientHeight || 0;
        const visibleCodes = reportElements
            .filter((item) => {
                const rect = item.nativeElement.getBoundingClientRect();
                return rect.top <= viewportHeight + 360 && rect.bottom >= -360;
            })
            .map((item) => item.nativeElement.getAttribute("data-report-code"))
            .filter((code): code is string => !!code);

        this.emitVisibleReportCodes(visibleCodes);
    }

    private emitVisibleReportCodes(reportCodes: string[]): void {
        const reportsByCode = new Map(
            (this.reports || []).map((report) => [report.code, report]),
        );
        const codesToLoad = Array.from(new Set(reportCodes || [])).filter(
            (code) => {
                const report = reportsByCode.get(code);

                if (!report || report.data || this.isReportLoaded(report)) {
                    return false;
                }

                if (this.emittedVisibleReportCodes.has(code)) {
                    return false;
                }

                this.emittedVisibleReportCodes.add(code);
                return true;
            },
        );

        if (codesToLoad.length) {
            this.reportsVisible.emit(codesToLoad);
        }
    }

    getReportFilters(report: DynamicDashboardReport): DynamicDashboardFilter[] {
        const configuredFilters = Array.isArray(report.filters)
            ? report.filters
            : [];
        const requiredFilters = this.requiredFiltersByCode[report.code] || [];
        const configuredKeys = new Set(
            configuredFilters.map((filter) => filter.key),
        );

        return [
            ...configuredFilters,
            ...requiredFilters.filter((filter) => !configuredKeys.has(filter.key)),
        ];
    }

    hasReportFilters(report: DynamicDashboardReport): boolean {
        return this.getReportFilters(report).length > 0;
    }

    shouldShowReportPeriodFilter(report: DynamicDashboardReport): boolean {
        if (this.shouldShowReportSingleDateFilter(report)) {
            return false;
        }

        if (typeof report.usesPeriodFilter === "boolean") {
            return report.usesPeriodFilter;
        }

        if (this.dateIndependentReportCodes.has(report.code)) {
            return false;
        }

        return !this.yearOnlyReportCodes.has(report.code);
    }

    shouldShowReportSingleDateFilter(report: DynamicDashboardReport): boolean {
        return this.ownCurrentDayReportCodes.has(report.code);
    }

    shouldShowDashboardMonthYearBadge(report: DynamicDashboardReport): boolean {
        if (typeof report.usesDashboardMonthYearFilter === "boolean") {
            return report.usesDashboardMonthYearFilter;
        }

        if (this.dateIndependentReportCodes.has(report.code)) {
            return false;
        }

        return this.yearOnlyReportCodes.has(report.code);
    }

    getAppliedFilterBadges(
        report: DynamicDashboardReport,
    ): DynamicDashboardFilterBadge[] {
        const badges: DynamicDashboardFilterBadge[] = [];
        const currentValues = this.filterValuesByCode[report.code] || {};
        const periodValues = this.getReportPeriodValues(report);
        const startDate = periodValues.startDate;
        const endDate = periodValues.endDate;
        const formattedPeriod = this.formatPeriodBadge(startDate, endDate);

        if (this.shouldShowReportSingleDateFilter(report)) {
            const formattedDate = this.formatDateForDisplay(endDate);

            if (formattedDate) {
                badges.push({
                    label: "Data",
                    value: formattedDate,
                });
            }
        } else if (this.shouldShowReportPeriodFilter(report) && formattedPeriod) {
            badges.push({
                label: "Período",
                value: formattedPeriod,
            });
        }

        if (this.shouldShowDashboardMonthYearBadge(report)) {
            badges.push({
                label: "Mês/Ano",
                value: this.formatDashboardYearMonthBadge(),
            });
        }

        if (this.filterData?.company) {
            badges.push({
                label: "Empresa",
                value: this.getEntityLabel(this.filterData.company),
            });
        }

        if (this.filterData?.performer) {
            badges.push({
                label: "Profissional",
                value: this.getEntityLabel(this.filterData.performer),
            });
        }

        this.getReportFilters(report).forEach((filter) => {
            const value = currentValues[filter.key];
            const formattedValue = this.formatFilterBadgeValue(filter, value);

            if (formattedValue) {
                badges.push({
                    label: filter.label,
                    value: formattedValue,
                });
            }
        });

        return badges;
    }

    isListView(report: DynamicDashboardReport): boolean {
        return this.viewModeByCode[report.code] === "list";
    }

    toggleReportView(report: DynamicDashboardReport): void {
        this.viewModeByCode[report.code] = this.isListView(report)
            ? "chart"
            : "list";
    }

    areReportFiltersVisible(report: DynamicDashboardReport): boolean {
        return !!this.filtersVisibleByCode[report.code];
    }

    shouldShowReportFilterToggle(report: DynamicDashboardReport): boolean {
        return (
            this.filtersEnabled &&
            (this.shouldShowReportSingleDateFilter(report) ||
                this.shouldShowReportPeriodFilter(report) ||
                this.hasReportFilters(report))
        );
    }

    toggleReportFilters(report: DynamicDashboardReport): void {
        if (!this.filtersEnabled) {
            return;
        }

        this.filtersVisibleByCode[report.code] =
            !this.areReportFiltersVisible(report);
    }

    getReportGridClass(report: DynamicDashboardReport): string {
        const columns = this.getEffectiveLayoutColumns(report);

        return `dynamic-report-grid-item--per-row-${columns}`;
    }

    isCurrencyValue(report: DynamicDashboardReport): boolean {
        return report.valueType === "BRL";
    }

    getCompatibleChartTypes(
        report: DynamicDashboardReport,
    ): DynamicDashboardChartTypeOption[] {
        const currentType = this.normalizeChartType(report.chartType);
        const compatibleGroup = this.compatibleChartTypeGroups.find((group) =>
            group.includes(currentType),
        );

        if (!compatibleGroup) {
            return [];
        }

        return [
            currentType,
            ...compatibleGroup.filter((chartType) => chartType !== currentType),
        ]
            .map((chartType) => this.chartTypeOptions[chartType])
            .filter((option) => !!option);
    }

    hasCompatibleChartTypeOptions(report: DynamicDashboardReport): boolean {
        return this.getCompatibleChartTypes(report).length > 1;
    }

    getSelectedChartType(report: DynamicDashboardReport): string {
        return this.normalizeChartType(report.chartType);
    }

    onReportChartTypeChange(
        report: DynamicDashboardReport,
        chartType: string,
    ): void {
        const normalizedChartType = this.normalizeChartType(chartType);

        if (!normalizedChartType || normalizedChartType === this.getSelectedChartType(report)) {
            return;
        }

        this.chartTypeChange.emit({
            reportCode: report.code,
            chartType: normalizedChartType,
        });
    }

    getReportFilterValue(
        report: DynamicDashboardReport,
        filter: DynamicDashboardFilter,
    ): any {
        return this.filterValuesByCode[report.code]?.[filter.key];
    }

    getReportPeriodStart(report: DynamicDashboardReport): Date | null {
        return this.toDate(this.getReportPeriodValues(report).startDate);
    }

    getReportPeriodEnd(report: DynamicDashboardReport): Date | null {
        return this.toDate(this.getReportPeriodValues(report).endDate);
    }

    getReportSingleDate(report: DynamicDashboardReport): Date | null {
        return this.getReportPeriodEnd(report);
    }

    onReportPeriodChange(
        report: DynamicDashboardReport,
        key: "startDate" | "endDate",
        value: any,
        rawValue?: string,
    ): void {
        const currentValues = {
            ...(this.filterValuesByCode[report.code] || {}),
        };
        const formattedValue = this.formatDate(value) || this.formatDate(rawValue);
        const rawValueProvided = rawValue !== undefined;
        const hasRawValue =
            rawValueProvided && rawValue != null && rawValue.toString().trim() !== "";

        if (formattedValue) {
            currentValues[key] = formattedValue;
        } else if (rawValueProvided && !hasRawValue) {
            delete currentValues[key];
        } else {
            return;
        }

        this.filterValuesByCode[report.code] = currentValues;

        if (key === "startDate") {
            return;
        }

        const fallbackValues = this.getReportDefaultPeriodValues(report);
        const startDate =
            currentValues.startDate ||
            fallbackValues.startDate ||
            this.formatDate(this.filterData?.startDate);
        const endDate =
            currentValues.endDate ||
            fallbackValues.endDate ||
            this.formatDate(this.filterData?.endDate);

        if (!this.isValidPeriod(startDate, endDate)) {
            return;
        }

        this.filterValuesByCode[report.code] = {
            ...currentValues,
            startDate,
            endDate,
        };

        this.emitReportFiltersChange(report.code);
    }

    onReportSingleDateChange(
        report: DynamicDashboardReport,
        value: any,
        rawValue?: string,
    ): void {
        const formattedValue = this.formatDate(value) || this.formatDate(rawValue);
        const rawValueProvided = rawValue !== undefined;
        const hasRawValue =
            rawValueProvided && rawValue != null && rawValue.toString().trim() !== "";

        if (!formattedValue) {
            if (!rawValueProvided || hasRawValue) {
                return;
            }

            this.resetReportPeriodToDefault(report);
            return;
        }

        this.filterValuesByCode[report.code] = {
            ...(this.filterValuesByCode[report.code] || {}),
            startDate: formattedValue,
            endDate: formattedValue,
        };

        this.emitReportFiltersChange(report.code);
    }

    clearReportPeriod(
        report: DynamicDashboardReport,
        startInput?: HTMLInputElement,
        endInput?: HTMLInputElement,
    ): void {
        const currentValues = this.hasOwnDefaultPeriod(report)
            ? {
                  ...(this.filterValuesByCode[report.code] || {}),
                  ...this.getReportDefaultPeriodValues(report),
              }
            : { ...(this.filterValuesByCode[report.code] || {}) };

        if (!this.hasOwnDefaultPeriod(report)) {
            delete currentValues.startDate;
            delete currentValues.endDate;
        }

        if (startInput) {
            startInput.value = this.hasOwnDefaultPeriod(report)
                ? this.formatDateForInput(currentValues.startDate)
                : "";
        }

        if (endInput) {
            endInput.value = this.hasOwnDefaultPeriod(report)
                ? this.formatDateForInput(currentValues.endDate)
                : "";
        }

        this.filterValuesByCode[report.code] = currentValues;
        this.emitReportFiltersChange(report.code);
    }

    onReportFilterChange(
        report: DynamicDashboardReport,
        filter: DynamicDashboardFilter,
        value: any,
    ): void {
        const numberValue = Number(value);
        const normalizedValue =
            filter.type === "NUMBER"
                ? Number.isFinite(numberValue) && numberValue > 0
                    ? numberValue
                    : 1
                : value;

        this.filterValuesByCode[report.code] = {
            ...(this.filterValuesByCode[report.code] || {}),
            [filter.key]: normalizedValue,
        };

        this.emitReportFiltersChange(report.code);
    }

    isReportVisible(report: DynamicDashboardReport): boolean {
        if (!report || report.restrictionType === "H") {
            return false;
        }

        const selected = this.filterData?.filterTarget;

        if (!Array.isArray(selected) || selected.length === 0) {
            return true;
        }

        return selected.includes("ALL") || selected.includes(report.code);
    }

    isStatusCount(report: DynamicDashboardReport): boolean {
        return report.chartType === "STATUS_COUNT";
    }

    isBarHorizontal(report: DynamicDashboardReport): boolean {
        return (
            report.chartType === "BAR_HORIZONTAL_VALUE" ||
            report.chartType === "BAR_HORIZONTAL_PERCENT"
        );
    }

    isColumnValue(report: DynamicDashboardReport): boolean {
        return report.chartType === "COLUMN_VALUE";
    }

    isLine(report: DynamicDashboardReport): boolean {
        return (
            report.chartType === "LINE_VALUE" ||
            report.chartType === "LINE_PERCENT"
        );
    }

    isLineMultiple(report: DynamicDashboardReport): boolean {
        return (
            report.chartType === "LINE_MULTIPLE" ||
            report.chartType === "LINE_MULTIPLE_VALUE" ||
            report.chartType === "LINE_MULTI_SERIES_VALUE" ||
            report.chartType === "COLUMN_MULTIPLE_VALUE"
        );
    }

    isPolarArea(report: DynamicDashboardReport): boolean {
        return report.chartType === "POLAR_AREA";
    }

    isPolarAreaGroup(report: DynamicDashboardReport): boolean {
        return report.chartType === "POLAR_AREA_GROUP";
    }

    isColumnPercentGroup(report: DynamicDashboardReport): boolean {
        return report.chartType === "COLUMN_PERCENT_GROUP";
    }

    isRadialBar(report: DynamicDashboardReport): boolean {
        return report.chartType === "RADIAL_BAR";
    }

    isTable(report: DynamicDashboardReport): boolean {
        return report.chartType === "TABLE_VALUE";
    }

    hasReportRows(report: DynamicDashboardReport): boolean {
        if (this.isTable(report)) {
            return this.getTableColumns(report).length > 0;
        }

        return this.getReportListRows(report).length > 0;
    }

    getReportListRows(report: DynamicDashboardReport): DynamicDashboardListRow[] {
        if (!report?.data) {
            return [];
        }

        if (this.isStatusCount(report) && Array.isArray(report.data)) {
            return report.data.map((item) => ({
                label: item.label,
                value: item.value,
                detail: item.detail ?? item.status,
            }));
        }

        if (this.isLineMultiple(report)) {
            const rows: DynamicDashboardListRow[] = [];

            if (this.isNamedSeriesArray(report.data.series)) {
                (report.data.categories || []).forEach((label, index) => {
                    report.data.series.forEach((serie) => {
                        rows.push({
                            label,
                            value: serie.data?.[index] ?? 0,
                            detail: serie.name,
                        });
                    });
                });

                return rows;
            }

            if (this.isEncodedMultiSeriesReport(report)) {
                const parsedSeries = this.buildEncodedMultiSeries(report);

                parsedSeries.categories.forEach((label, index) => {
                    parsedSeries.series.forEach((serie) => {
                        rows.push({
                            label,
                            value: serie.data?.[index] ?? 0,
                            detail: serie.name,
                        });
                    });
                });

                return rows;
            }

            (report.data.categories || []).forEach((label, index) => {
                rows.push({
                    label,
                    value: report.data.appointmentsSeries?.[index] ?? 0,
                    detail: this.getLineMultipleSeriesName(report, 0),
                });

                rows.push({
                    label,
                    value: report.data.absencesSeries?.[index] ?? 0,
                    detail: this.getLineMultipleSeriesName(report, 1),
                });
            });

            return rows;
        }

        if (this.isColumnPercentGroup(report) && Array.isArray(report.data)) {
            const rows: DynamicDashboardListRow[] = [];

            report.data.forEach((chart) => {
                (chart.xaxisCategories || []).forEach((label, index) => {
                    rows.push({
                    group: chart.title,
                    label,
                    value: chart.seriesData?.[index] ?? 0,
                    detail: `${chart.occupiedSlots?.[index] ?? 0} de ${chart.totalSlots?.[index] ?? 0}`,
                    });
                });
            });

            return rows;
        }

        if (this.isPolarAreaGroup(report) && Array.isArray(report.data)) {
            const rows: DynamicDashboardListRow[] = [];

            report.data.forEach((chart) => {
                (chart.labels || []).forEach((label, index) => {
                    rows.push({
                        group: chart.title,
                        label,
                        value: chart.series?.[index] ?? 0,
                    });
                });
            });

            return rows;
        }

        if (this.isRadialBar(report) && Array.isArray(report.data)) {
            return report.data.map((item) => ({
                group: item.title,
                label: item.label,
                value: item.value,
                detail: `${item.totalAbsences ?? 0} de ${item.totalAppointments ?? 0}`,
            }));
        }

        if (this.isPolarArea(report)) {
            return (report.data.labels || []).map((label, index) => ({
                label,
                value: report.data.series?.[index] ?? 0,
            }));
        }

        if (report.data.categories) {
            return (report.data.categories || []).map((label, index) => ({
                label,
                value:
                    report.data.totalValues?.[index] ??
                    report.data.series?.[index] ??
                    0,
                detail:
                    report.data.totalAppointments?.[index] != null
                        ? `${report.data.totalAbsences?.[index] ?? 0} de ${report.data.totalAppointments?.[index] ?? 0}`
                        : undefined,
            }));
        }

        return [];
    }

    formatListValue(
        report: DynamicDashboardReport,
        row: DynamicDashboardListRow,
    ): string {
        const value = this.normalizeNumber(row.value);

        if (report.valueType === "BRL") {
            return this.formatValue(report, value);
        }

        if (report.valueType === "PERCENT" || report.chartType.includes("PERCENT")) {
            return `${value.toFixed(2)}%`;
        }

        if (this.isDurationMinutesReport(report)) {
            return this.formatMinutes(value);
        }

        return new Intl.NumberFormat("pt-BR").format(value);
    }

    getStatusColor(status: any): string {
        const value = (status?.status ?? status?.statusValue ?? "")
            .toString()
            .toUpperCase()
            .trim();

        return this.statusColorMap[value] ?? "#546E7A";
    }

    getStatusCardIcon(report: DynamicDashboardReport): string {
        if (report.valueType === "BRL" || report.type === "FT") {
            return "attach_money";
        }

        if (report.type === "DS") {
            return "event";
        }

        if (report.type === "OS") {
            return "assignment";
        }

        return "insights";
    }

    getStatusCardSubtitle(report: DynamicDashboardReport): string | null {
        if (report.valueType === "BRL") {
            return "Valor total";
        }

        if (report.valueType === "PERCENT" || report.chartType.includes("PERCENT")) {
            return "Percentual";
        }

        if (report.type === "DS") {
            return "Agendamentos";
        }

        return "Total";
    }

    getStatusGridStyle(report: DynamicDashboardReport): Record<string, string> {
        return {
            "--status-card-width": `${this.getStatusCardWidth(report)}px`,
        };
    }

    formatStatusCardValue(report: DynamicDashboardReport, status: any): string {
        const value = this.normalizeNumber(status?.value);

        if (report.valueType === "BRL") {
            return this.formatValue(report, value);
        }

        if (report.valueType === "PERCENT" || report.chartType.includes("PERCENT")) {
            return `${value.toFixed(2)}%`;
        }

        return new Intl.NumberFormat("pt-BR").format(value);
    }

    private getStatusCardWidth(report: DynamicDashboardReport): number {
        if (!Array.isArray(report?.data) || report.data.length === 0) {
            return 190;
        }

        const widths = report.data.map((status) => {
            const titleWidth = this.estimateTextWidth(status?.label, 7.3, 64);
            const valueWidth = this.estimateTextWidth(
                this.formatStatusCardValue(report, status),
                report.valueType === "BRL" ? 8.5 : 9.5,
                34,
            );
            const detailWidth = this.estimateTextWidth(status?.detail, 6.8, 34);

            return Math.max(titleWidth, valueWidth, detailWidth);
        });

        return Math.ceil(Math.max(190, ...widths));
    }

    private estimateTextWidth(
        value: any,
        averageCharacterWidth: number,
        extraWidth: number,
    ): number {
        const text = value != null ? `${value}` : "";
        return text.length * averageCharacterWidth + extraWidth;
    }

    formatTotal(report: DynamicDashboardReport): string {
        const total =
            this.getFinancialResultTotal(report) ??
            report?.data?.totalGeneral ??
            0;
        return this.formatValue(report, total);
    }

    private getFinancialResultTotal(report: DynamicDashboardReport): number | null {
        if (report?.code !== "dashboards-financial-result") {
            return null;
        }

        const categories = report.data?.categories || [];
        const values = this.normalizeNumberArray(report.data?.totalValues);
        const resultIndex = categories.findIndex(
            (category) => `${category}`.toLowerCase() === "resultado",
        );

        if (resultIndex >= 0) {
            return this.normalizeNumber(values[resultIndex]);
        }

        const revenueIndex = categories.findIndex(
            (category) => `${category}`.toLowerCase() === "receita",
        );
        const expenseIndex = categories.findIndex(
            (category) => `${category}`.toLowerCase() === "despesa",
        );

        if (revenueIndex >= 0 && expenseIndex >= 0) {
            return (
                this.normalizeNumber(values[revenueIndex]) -
                this.normalizeNumber(values[expenseIndex])
            );
        }

        return null;
    }

    getChartSubtitleLines(report: DynamicDashboardReport): string[] {
        const subtitleLines: string[] = [];
        const productionComparisonEvolution =
            this.getProductionComparisonEvolutionText(report);

        if (
            report?.data?.totalGeneral != null &&
            !this.isAverageTicketReport(report) &&
            !this.isDurationMinutesReport(report)
        ) {
            subtitleLines.push(`Total: ${this.formatTotal(report)}`);
        }

        if (productionComparisonEvolution) {
            subtitleLines.push(productionComparisonEvolution);
        }

        return subtitleLines;
    }

    private isAverageTicketReport(report: DynamicDashboardReport): boolean {
        return `${report?.code || ""}`.includes("average-ticket");
    }

    getTableColumns(report: DynamicDashboardReport): DynamicDashboardTableColumn[] {
        if (Array.isArray(report?.data?.columns)) {
            return report.data.columns;
        }

        if (this.hasEncodedTableRows(report)) {
            return this.getDefaultTableColumns();
        }

        return [];
    }

    getTableRows(report: DynamicDashboardReport): any[] {
        if (Array.isArray(report?.data?.rows)) {
            return report.data.rows;
        }

        if (this.hasEncodedTableRows(report)) {
            return (report.data.categories || []).map((category) =>
                this.parseEncodedTableRow(category),
            );
        }

        return [];
    }

    getTableTotals(report: DynamicDashboardReport): Record<string, any> {
        if (report?.data?.totals) {
            return report.data.totals;
        }

        if (this.hasEncodedTableRows(report)) {
            return this.getTableRows(report).reduce((totals, row) => {
                this.getDefaultTableColumns()
                    .filter((column) => column.key !== "label")
                    .forEach((column) => {
                        totals[column.key] =
                            this.normalizeNumber(totals[column.key]) +
                            this.normalizeNumber(row[column.key]);
                    });

                return totals;
            }, {});
        }

        return {};
    }

    private getDefaultTableColumns(): DynamicDashboardTableColumn[] {
        return [
            { key: "label", label: "Convênio", type: "TEXT" },
            { key: "total", label: "Produzido", type: "BRL" },
            { key: "value_billing", label: "Faturado", type: "BRL" },
            { key: "value_not_billing", label: "A faturar", type: "BRL" },
            { key: "value_pending", label: "Pendente", type: "BRL" },
            { key: "value_blocked", label: "Bloqueado", type: "BRL" },
            { key: "value_received", label: "Recebido", type: "BRL" },
            {
                key: "value_not_received",
                label: "A receber (inclui glosas)",
                type: "BRL",
            },
            { key: "glosa", label: "Glosa", type: "BRL" },
            { key: "gloss_paid", label: "Recebida", type: "BRL" },
            { key: "gloss_accepted", label: "Acatada", type: "BRL" },
        ];
    }

    private hasEncodedTableRows(report: DynamicDashboardReport): boolean {
        return (
            this.isTable(report) &&
            Array.isArray(report?.data?.categories) &&
            report.data.categories.some((category) =>
                String(category).includes(this.tableValueDelimiter),
            )
        );
    }

    private parseEncodedTableRow(category: any): Record<string, any> {
        const values = String(category).split(this.tableValueDelimiter);
        const row: Record<string, any> = {};

        this.getDefaultTableColumns().forEach((column, index) => {
            row[column.key] =
                column.key === "label"
                    ? values[index] || ""
                    : this.normalizeNumber(values[index]);
        });

        return row;
    }

    formatTableValue(
        report: DynamicDashboardReport,
        column: DynamicDashboardTableColumn,
        row: any,
    ): string {
        const value = row?.[column.key];

        if (column.type === "BRL") {
            return this.formatValue(report, this.normalizeNumber(value));
        }

        return value ?? "";
    }

    formatTableTotal(
        report: DynamicDashboardReport,
        column: DynamicDashboardTableColumn,
    ): string {
        if (column.key === "label") {
            return "TOTAL GERAL";
        }

        const value = this.getTableTotals(report)?.[column.key];

        if (column.type === "BRL") {
            return this.formatValue(report, this.normalizeNumber(value));
        }

        return value ?? "";
    }

    private applyExpandCommand(force = false): void {
        if (!this.expandCommand) {
            return;
        }

        if (
            !force &&
            this.lastExpandCommandVersion === this.expandCommand.version
        ) {
            return;
        }

        this.visibleReports.forEach((report) => {
            this.expandedByCode[report.code] =
                this.expandCommand?.expanded === true &&
                !this.isReportDisabled(report);
        });

        if (this.expandCommand.expanded === true) {
            this.emitVisibleReportCodes(
                this.visibleReports.map((report) => report.code),
            );
        }

        this.lastExpandCommandVersion = this.expandCommand.version;
    }

    private buildChartOptions(): void {
        const chartOptionsByCode = { ...this.chartOptionsByCode };
        const reportRefsByCode: Record<string, DynamicDashboardReport> = {};
        const currentReportCodes = new Set<string>();

        (this.reports || []).forEach((report) => {
            currentReportCodes.add(report.code);
            reportRefsByCode[report.code] = report;

            if (this.expandedByCode[report.code] == null) {
                this.expandedByCode[report.code] =
                    report.restrictionType !== "D";
            }

            if (!report.data) {
                delete chartOptionsByCode[report.code];
                return;
            }

            if (
                this.previousReportRefsByCode[report.code] === report &&
                chartOptionsByCode[report.code]
            ) {
                return;
            }

            if (this.isStatusCount(report)) {
                delete chartOptionsByCode[report.code];
                return;
            }

            if (this.isTable(report)) {
                delete chartOptionsByCode[report.code];
                return;
            }

            if (this.isLineMultiple(report)) {
                chartOptionsByCode[report.code] =
                    this.createLineMultipleOptions(report);
                return;
            }

            if (this.isLine(report)) {
                chartOptionsByCode[report.code] =
                    this.createLineOptions(report);
                return;
            }

            if (this.isPolarAreaGroup(report)) {
                chartOptionsByCode[report.code] = report.data.map((item) =>
                    this.createPolarAreaOptions({ ...report, data: item }),
                );
                return;
            }

            if (this.isPolarArea(report)) {
                chartOptionsByCode[report.code] =
                    this.createPolarAreaOptions(report);
                return;
            }

            if (this.isColumnPercentGroup(report)) {
                chartOptionsByCode[report.code] = report.data.map((item) =>
                    this.createColumnPercentOptions(item),
                );
                return;
            }

            if (this.isColumnValue(report)) {
                chartOptionsByCode[report.code] =
                    this.createColumnValueOptions(report);
                return;
            }

            if (this.isRadialBar(report)) {
                chartOptionsByCode[report.code] = report.data.map((item) =>
                    this.createRadialBarOptions(item),
                );
                return;
            }

            chartOptionsByCode[report.code] = this.createBarHorizontalOptions(report);
        });

        Object.keys(chartOptionsByCode).forEach((code) => {
            if (!currentReportCodes.has(code)) {
                delete chartOptionsByCode[code];
            }
        });

        this.chartOptionsByCode = chartOptionsByCode;
        this.previousReportRefsByCode = reportRefsByCode;
    }

    private initializeFilterValues(): void {
        (this.reports || []).forEach((report) => {
            const currentValues = this.filterValuesByCode[report.code] || {};
            const defaultPeriodValues = this.getReportDefaultPeriodValues(report);

            if (
                defaultPeriodValues.startDate &&
                currentValues.startDate === undefined
            ) {
                currentValues.startDate = defaultPeriodValues.startDate;
            }

            if (
                defaultPeriodValues.endDate &&
                currentValues.endDate === undefined
            ) {
                currentValues.endDate = defaultPeriodValues.endDate;
            }

            this.getReportFilters(report).forEach((filter) => {
                if (currentValues[filter.key] !== undefined) {
                    return;
                }

                currentValues[filter.key] =
                    filter.defaultValue !== undefined
                        ? filter.defaultValue
                        : filter.type === "MULTI_SELECT"
                        ? []
                        : null;
            });

            this.filterValuesByCode[report.code] = currentValues;

            if (!this.viewModeByCode[report.code]) {
                this.viewModeByCode[report.code] = "chart";
            }
        });
    }

    private hasOwnDefaultPeriod(report: DynamicDashboardReport): boolean {
        return (
            this.ownCurrentMonthPeriodReportCodes.has(report.code) ||
            this.ownCurrentDayReportCodes.has(report.code)
        );
    }

    private getReportPeriodValues(report: DynamicDashboardReport): {
        startDate: any;
        endDate: any;
    } {
        const currentValues = this.filterValuesByCode[report.code] || {};
        const defaultValues = this.getReportDefaultPeriodValues(report);

        return {
            startDate:
                currentValues.startDate ||
                defaultValues.startDate ||
                this.filterData?.startDate,
            endDate:
                currentValues.endDate ||
                defaultValues.endDate ||
                this.filterData?.endDate,
        };
    }

    private getReportDefaultPeriodValues(report: DynamicDashboardReport): {
        startDate?: string;
        endDate?: string;
    } {
        if (this.ownCurrentMonthPeriodReportCodes.has(report.code)) {
            const today = new Date();
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);

            return {
                startDate: this.formatDate(start) || undefined,
                endDate: this.formatDate(end) || undefined,
            };
        }

        if (this.ownCurrentDayReportCodes.has(report.code)) {
            const today = this.formatDate(new Date()) || undefined;

            return {
                startDate: today,
                endDate: today,
            };
        }

        return {};
    }

    private resetReportPeriodToDefault(report: DynamicDashboardReport): void {
        if (!this.hasOwnDefaultPeriod(report)) {
            return;
        }

        this.filterValuesByCode[report.code] = {
            ...(this.filterValuesByCode[report.code] || {}),
            ...this.getReportDefaultPeriodValues(report),
        };

        this.emitReportFiltersChange(report.code);
    }

    private createLimitRowsFilter(): DynamicDashboardFilter {
        return {
            key: "limitRows",
            label: "Quantidade de registros",
            type: "NUMBER",
            defaultValue: 10,
        };
    }

    private formatDashboardYearMonthBadge(): string {
        const month = Number(this.filterData?.dashboardMonth) || new Date().getMonth() + 1;
        const year = Number(this.filterData?.dashboardYear) || new Date().getFullYear();
        const monthLabel =
            [
                "Jan",
                "Fev",
                "Mar",
                "Abr",
                "Mai",
                "Jun",
                "Jul",
                "Ago",
                "Set",
                "Out",
                "Nov",
                "Dez",
            ][Math.max(0, Math.min(month - 1, 11))] || `${month}`;

        return `${monthLabel}/${year}`;
    }

    private formatPeriodBadge(startValue: any, endValue: any): string | null {
        const startDate = this.formatDateForDisplay(startValue);
        const endDate = this.formatDateForDisplay(endValue);

        if (startDate && endDate) {
            return `${startDate} a ${endDate}`;
        }

        if (startDate) {
            return `A partir de ${startDate}`;
        }

        if (endDate) {
            return `Até ${endDate}`;
        }

        return null;
    }

    private formatDateForDisplay(value: any): string | null {
        const date = this.toDate(value);

        if (!date) {
            return null;
        }

        const day = `${date.getDate()}`.padStart(2, "0");
        const month = `${date.getMonth() + 1}`.padStart(2, "0");

        return `${day}/${month}/${date.getFullYear()}`;
    }

    private formatDateForInput(value: any): string {
        return this.formatDate(value) || "";
    }

    private formatFilterBadgeValue(
        filter: DynamicDashboardFilter,
        value: any,
    ): string | null {
        if (
            value === null ||
            value === undefined ||
            value === "" ||
            (Array.isArray(value) && value.length === 0)
        ) {
            return null;
        }

        if (Array.isArray(value)) {
            const labels = value.map((item) =>
                this.getFilterOptionLabel(filter, item),
            );

            return labels.length > 3
                ? `${labels.slice(0, 3).join(", ")} +${labels.length - 3}`
                : labels.join(", ");
        }

        return this.getFilterOptionLabel(filter, value);
    }

    private getFilterOptionLabel(
        filter: DynamicDashboardFilter,
        value: any,
    ): string {
        const option = (filter.options || []).find(
            (item) => `${item.value}` === `${value}`,
        );

        return option?.label ?? `${value}`;
    }

    private getEntityLabel(value: any): string {
        if (!value || typeof value !== "object") {
            return value ? `${value}` : "";
        }

        return (
            value.name ||
            value.description ||
            value.fantasyName ||
            value.businessName ||
            value.fullName ||
            value.label ||
            `${value.id || ""}`
        );
    }

    private formatDate(value: any): string | null {
        if (!value) {
            return null;
        }

        if (typeof value?.format === "function") {
            return value.format("YYYY-MM-DD");
        }

        if (typeof value === "string") {
            const parsedDate = this.parseDateText(value);

            if (parsedDate) {
                return parsedDate;
            }
        }

        const date = value instanceof Date ? value : new Date(value);

        if (Number.isNaN(date.getTime())) {
            return null;
        }

        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const day = `${date.getDate()}`.padStart(2, "0");

        return `${date.getFullYear()}-${month}-${day}`;
    }

    private parseDateText(value: string): string | null {
        const trimmedValue = value.trim();
        const brDate = trimmedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        const isoDate = trimmedValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);

        if (brDate) {
            return this.formatDateParts(
                Number(brDate[3]),
                Number(brDate[2]),
                Number(brDate[1]),
            );
        }

        if (isoDate) {
            return this.formatDateParts(
                Number(isoDate[1]),
                Number(isoDate[2]),
                Number(isoDate[3]),
            );
        }

        return null;
    }

    private formatDateParts(year: number, month: number, day: number): string | null {
        const date = new Date(year, month - 1, day);

        if (
            date.getFullYear() !== year ||
            date.getMonth() !== month - 1 ||
            date.getDate() !== day
        ) {
            return null;
        }

        return `${year}-${`${month}`.padStart(2, "0")}-${`${day}`.padStart(2, "0")}`;
    }

    private toDate(value: any): Date | null {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return value;
        }

        if (typeof value?.toDate === "function") {
            return value.toDate();
        }

        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [year, month, day] = value.split("-").map(Number);
            return new Date(year, month - 1, day);
        }

        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    private emitReportFiltersChange(reportCode: string): void {
        const filters: Record<string, any> = {};

        Object.keys(this.filterValuesByCode || {}).forEach((code) => {
            const cleanedValues = this.cleanReportFilterValues(
                this.filterValuesByCode[code],
            );

            if (Object.keys(cleanedValues).length > 0) {
                filters[code] = cleanedValues;
            }
        });

        this.filtersChange.emit({
            filters,
            reportCodes: reportCode ? [reportCode] : [],
        });
    }

    private cleanReportFilterValues(values: Record<string, any>): Record<string, any> {
        const cleanedValues: Record<string, any> = {};

        Object.keys(values || {}).forEach((key) => {
            const value = values[key];

            if (
                value === null ||
                value === undefined ||
                value === "" ||
                (Array.isArray(value) && value.length === 0)
            ) {
                return;
            }

            cleanedValues[key] = value;
        });

        return cleanedValues;
    }

    private isValidPeriod(startDate: string | null, endDate: string | null): boolean {
        if (!startDate || !endDate) {
            return false;
        }

        const start = this.toDate(startDate);
        const end = this.toDate(endDate);

        return !!start && !!end && start.getTime() <= end.getTime();
    }

    private isCurrencyLineMultiple(report: DynamicDashboardReport): boolean {
        return (
            report.valueType === "BRL" &&
            (report.chartType === "LINE_MULTIPLE_VALUE" ||
                report.chartType === "LINE_MULTI_SERIES_VALUE" ||
                report.chartType === "COLUMN_MULTIPLE_VALUE" ||
                report.chartType === "LINE_MULTIPLE")
        );
    }

    private isDurationMinutesReport(report: DynamicDashboardReport): boolean {
        return report?.code === "dashboards-order-patient-stay-average";
    }

    private formatMinutes(value: number): string {
        return `${new Intl.NumberFormat("pt-BR", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 0,
        }).format(this.normalizeNumber(value))}min`;
    }

    private getLineMultipleSeriesName(
        report: DynamicDashboardReport,
        index: number,
    ): string {
        if (this.isCurrencyLineMultiple(report)) {
            const selectedYear =
                Number(this.filterData?.dashboardYear) || new Date().getFullYear();
            const year = index === 0 ? selectedYear : selectedYear - 1;

            return `${year}`;
        }

        return index === 0 ? "Agendamentos" : "Faltas";
    }

    private getProductionComparisonEvolutionText(
        report: DynamicDashboardReport,
    ): string | null {
        const comparison = report?.data?.comparison;

        if (!comparison) {
            return null;
        }

        const currentTotal = this.normalizeNumber(comparison.currentTotal);
        const comparisonTotal = this.normalizeNumber(comparison.comparisonTotal);
        let evolution =
            comparison.evolutionPercent == null
                ? null
                : this.normalizeNumber(comparison.evolutionPercent);

        if (
            comparisonTotal === 0 &&
            currentTotal !== 0 &&
            (evolution === null || evolution === 0)
        ) {
            evolution = currentTotal > 0 ? 100 : -100;
        }

        if (evolution === null) {
            return null;
        }

        const formattedEvolution = new Intl.NumberFormat("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(evolution);
        const sign = evolution > 0 ? "+" : "";

        return `${sign}${formattedEvolution}%`;
    }

    private normalizeNumber(value: any): number {
        const numberValue = Number(value);
        return Number.isFinite(numberValue) ? numberValue : 0;
    }

    private normalizeChartType(chartType: any): string {
        return chartType != null
            ? `${chartType}`.trim().toUpperCase()
            : "";
    }

    private normalizeLayoutColumns(value: any): number {
        const columns = Number(value);

        if (!Number.isFinite(columns)) {
            return 1;
        }

        return Math.max(1, Math.min(Math.round(columns), 4));
    }

    private getEffectiveLayoutColumns(report: DynamicDashboardReport): number {
        const requestedColumns = this.normalizeLayoutColumns(report.layoutColumns);
        const maximumColumns = this.getMaximumLayoutColumns(report);

        return Math.min(requestedColumns, maximumColumns);
    }

    private getMaximumLayoutColumns(report: DynamicDashboardReport): number {
        if (this.isTable(report)) {
            return 1;
        }

        if (this.isSmallViewport()) {
            return 1;
        }

        if (this.isBarHorizontal(report)) {
            return 2;
        }

        if (this.isHdViewport()) {
            return 2;
        }

        return 3;
    }

    private getAdaptiveChartHeight(
        report: DynamicDashboardReport,
        preferredHeight: number,
    ): number {
        const columns = this.getEffectiveLayoutColumns(report);

        if (this.isSmallViewport()) {
            return Math.min(preferredHeight, window.innerWidth <= 420 ? 270 : 300);
        }

        if (columns >= 3) {
            return Math.min(preferredHeight, 220);
        }

        if (columns === 2 || this.isHdViewport()) {
            return Math.min(preferredHeight, 250);
        }

        return preferredHeight;
    }

    private getAdaptiveGroupedChartHeight(preferredHeight: number): number {
        return this.isHdViewport() ? Math.min(preferredHeight, 250) : preferredHeight;
    }

    private isHdViewport(): boolean {
        return typeof window !== "undefined" && window.innerWidth <= 1366;
    }

    private isSmallViewport(): boolean {
        return typeof window !== "undefined" && window.innerWidth <= 959;
    }

    private normalizeNumberArray(values: any): number[] {
        return Array.isArray(values)
            ? values.map((value) => this.normalizeNumber(value))
            : [];
    }

    private normalizeChartCategories(values: any): string[] {
        if (!Array.isArray(values)) {
            return [];
        }

        return values.map((value, index) => {
            const category = value != null ? `${value}`.trim() : "";
            return category || `Item ${index + 1}`;
        });
    }

    private abbreviateAxisLabel(value: any, limit = 14): string {
        const label = value != null ? `${value}`.trim() : "";

        if (label.length <= limit) {
            return label;
        }

        return `${label.substring(0, limit - 3)}...`;
    }

    private normalizeChartSeriesData(values: any, size: number): number[] {
        const normalizedValues = this.normalizeNumberArray(values);
        const expectedSize = Math.max(size, normalizedValues.length);

        return Array.from({ length: expectedSize }, (_, index) =>
            this.normalizeNumber(normalizedValues[index]),
        );
    }

    private isNamedSeriesArray(values: any): boolean {
        return (
            Array.isArray(values) &&
            values.some((item) => item && typeof item === "object" && Array.isArray(item.data))
        );
    }

    private isEncodedMultiSeriesReport(report: DynamicDashboardReport): boolean {
        return (
            report.chartType === "LINE_MULTI_SERIES_VALUE" &&
            Array.isArray(report.data?.categories) &&
            report.data.categories.some((category) =>
                typeof category === "string" && category.includes("||"),
            )
        );
    }

    private buildEncodedMultiSeries(report: DynamicDashboardReport): {
        categories: string[];
        series: Array<{ name: string; data: number[] }>;
    } {
        const categories: string[] = [];
        const categoryIndexes: Record<string, number> = {};
        const seriesByName: Record<string, number[]> = {};
        const values = Array.isArray(report.data?.totalValues)
            ? report.data.totalValues
            : report.data?.series;

        (report.data?.categories || []).forEach((category, index) => {
            const parts = `${category}`.split("||");
            const seriesName = parts[0] || "Sem grupo";
            const label = parts[2] || parts[1] || category;

            if (categoryIndexes[label] === undefined) {
                categoryIndexes[label] = categories.length;
                categories.push(label);
                Object.keys(seriesByName).forEach((name) => {
                    seriesByName[name].push(0);
                });
            }

            if (!seriesByName[seriesName]) {
                seriesByName[seriesName] = Array.from(
                    { length: categories.length },
                    () => 0,
                );
            }

            seriesByName[seriesName][categoryIndexes[label]] =
                this.normalizeNumber(values?.[index]);
        });

        return {
            categories,
            series: Object.keys(seriesByName).map((name) => ({
                name,
                data: seriesByName[name],
            })),
        };
    }

    private hasChartSeriesData(series: any[]): boolean {
        return (series || []).some((item) =>
            (item.data || []).some((value) => Number.isFinite(Number(value))),
        );
    }

    private getChartColors(total: number = this.chartColorPalette.length): string[] {
        const colorCount = Math.max(total, this.chartColorPalette.length);

        return Array.from({ length: colorCount }, (_, index) =>
            this.chartColorPalette[index % this.chartColorPalette.length],
        );
    }

    private createBarHorizontalOptions(report: DynamicDashboardReport): any {
        const data = report.data;

        if (this.isFinancialResultBar(report)) {
            return this.createFinancialResultBarOptions(report);
        }

        if (this.isNumberBarHorizontalValue(report)) {
            return this.createNumberBarHorizontalOptions(report);
        }

        return {
            series: [{ data: this.normalizeNumberArray(data.series) }],
            colors: this.getChartColors(data.categories?.length),
            chart: {
                type: "bar",
                height: this.getAdaptiveChartHeight(report, 420),
                width: "100%",
                redrawOnParentResize: true,
                redrawOnWindowResize: true,
            },
            plotOptions: {
                bar: {
                    borderRadius: 4,
                    borderRadiusApplication: "end",
                    distributed: true,
                    horizontal: true,
                },
            },
            dataLabels: {
                enabled: true,
                formatter: (val: number) =>
                    `${this.normalizeNumber(val).toFixed(2)}%`,
            },
            xaxis: {
                categories: data.categories ?? [],
            },
            tooltip: {
                y: {
                    formatter: (val: number, opts: any) => {
                        const index = opts.dataPointIndex;

                        if (report.chartType === "BAR_HORIZONTAL_PERCENT") {
                            const absences = data.totalAbsences?.[index] ?? 0;
                            const total = data.totalAppointments?.[index] ?? 0;
                            return `${this.normalizeNumber(val).toFixed(2)}% - ${absences} de ${total}`;
                        }

                        const totalValue = data.totalValues?.[index] ?? 0;
                        return `${this.normalizeNumber(val).toFixed(2)}% - ${this.formatValue(report, totalValue)}`;
                    },
                },
            },
        };
    }

    private isFinancialResultBar(report: DynamicDashboardReport): boolean {
        return (
            report.chartType === "BAR_HORIZONTAL_VALUE" &&
            report.code === "dashboards-financial-result"
        );
    }

    private isNumberBarHorizontalValue(report: DynamicDashboardReport): boolean {
        return (
            report.chartType === "BAR_HORIZONTAL_VALUE" &&
            report.valueType === "NUMBER"
        );
    }

    private createNumberBarHorizontalOptions(report: DynamicDashboardReport): any {
        const data = report.data;
        const values = data.totalValues?.length ? data.totalValues : data.series;

        return {
            series: [{ data: this.normalizeNumberArray(values) }],
            colors: this.getChartColors(data.categories?.length),
            chart: {
                type: "bar",
                height: this.getAdaptiveChartHeight(report, 420),
                width: "100%",
                redrawOnParentResize: true,
                redrawOnWindowResize: true,
            },
            plotOptions: {
                bar: {
                    borderRadius: 4,
                    borderRadiusApplication: "end",
                    distributed: true,
                    horizontal: true,
                },
            },
            dataLabels: {
                enabled: true,
                formatter: (val: number) =>
                    this.formatValue(report, this.normalizeNumber(val)),
            },
            xaxis: {
                categories: data.categories ?? [],
                labels: {
                    formatter: (val: number) =>
                        this.formatValue(report, this.normalizeNumber(val)),
                },
            },
            tooltip: {
                y: {
                    formatter: (val: number) =>
                        this.formatValue(report, this.normalizeNumber(val)),
                },
            },
        };
    }

    private createFinancialResultBarOptions(report: DynamicDashboardReport): any {
        const data = report.data;

        return {
            series: [{ data: this.normalizeNumberArray(data.totalValues) }],
            colors: this.getChartColors(data.categories?.length),
            chart: {
                type: "bar",
                height: this.getAdaptiveChartHeight(report, 420),
                width: "100%",
                redrawOnParentResize: true,
                redrawOnWindowResize: true,
            },
            plotOptions: {
                bar: {
                    borderRadius: 4,
                    borderRadiusApplication: "end",
                    distributed: true,
                    horizontal: true,
                },
            },
            dataLabels: {
                enabled: true,
                formatter: (val: number) =>
                    this.formatValue(report, this.normalizeNumber(val)),
            },
            xaxis: {
                categories: data.categories ?? [],
                labels: {
                    formatter: (val: number) =>
                        this.formatValue(report, this.normalizeNumber(val)),
                },
            },
            tooltip: {
                y: {
                    formatter: (val: number) =>
                        this.formatValue(report, this.normalizeNumber(val)),
                },
            },
        };
    }

    private createColumnPercentOptions(data: any): any {
        return {
            series: [
                {
                    name: data.seriesName || "Percentual",
                    data: this.normalizeNumberArray(data.seriesData),
                },
            ],
            colors: this.getChartColors(data.xaxisCategories?.length),
            chart: {
                height: this.getAdaptiveGroupedChartHeight(350),
                type: "bar",
                width: "100%",
                redrawOnParentResize: true,
                redrawOnWindowResize: true,
            },
            plotOptions: {
                bar: {
                    columnWidth: "50%",
                    borderRadius: 6,
                    distributed: true,
                    dataLabels: {
                        position: "top",
                    },
                },
            },
            dataLabels: {
                enabled: true,
                formatter: (val: number) =>
                    `${this.normalizeNumber(val).toFixed(2)}%`,
                offsetY: -20,
            },
            yaxis: {
                labels: {
                    formatter: (val: number) =>
                        `${this.normalizeNumber(val).toFixed(0)}%`,
                },
            },
            xaxis: {
                categories: data.xaxisCategories ?? [],
                labels: {
                    rotate: -35,
                    trim: true,
                    hideOverlappingLabels: true,
                },
            },
            legend: {
                show: false,
            },
            tooltip: {
                y: {
                    formatter: (val: number, opts: any) => {
                        const index = opts.dataPointIndex;
                        const occupied = data.occupiedSlots?.[index] ?? 0;
                        const total = data.totalSlots?.[index] ?? 0;
                        return `${this.normalizeNumber(val).toFixed(2)}% - ${occupied} de ${total}`;
                    },
                },
            },
        };
    }

    private createColumnValueOptions(report: DynamicDashboardReport): any {
        const data = report.data;
        const values = data.totalValues?.length ? data.totalValues : data.series;
        const categories = this.normalizeChartCategories(data.categories);

        return {
            series: [
                {
                    name: report.name,
                    data: this.normalizeNumberArray(values),
                },
            ],
            colors: this.getChartColors(data.categories?.length),
            chart: {
                type: "bar",
                height: this.getAdaptiveChartHeight(report, 275),
                width: "100%",
                redrawOnParentResize: true,
                redrawOnWindowResize: true,
                toolbar: {
                    show: true,
                },
            },
            grid: {
                padding: {
                    top: 8,
                    right: 8,
                    bottom: 0,
                    left: 4,
                },
            },
            plotOptions: {
                bar: {
                    columnWidth: "52%",
                    borderRadius: 4,
                    borderRadiusApplication: "end",
                    distributed: true,
                    dataLabels: {
                        position: "top",
                    },
                },
            },
            dataLabels: {
                enabled: true,
                formatter: (val: number) =>
                    this.formatValue(report, this.normalizeNumber(val)),
                offsetY: -18,
                style: {
                    fontSize: "10px",
                },
            },
            yaxis: {
                labels: {
                    formatter: (val: number) =>
                        this.formatValue(report, this.normalizeNumber(val)),
                },
            },
            xaxis: {
                categories,
                labels: {
                    rotate: -35,
                    rotateAlways: true,
                    trim: true,
                    hideOverlappingLabels: false,
                    maxHeight: 72,
                    formatter: (value: string) =>
                        this.abbreviateAxisLabel(value),
                    style: {
                        fontSize: "10px",
                    },
                },
                tooltip: {
                    enabled: true,
                },
            },
            legend: {
                show: false,
            },
            tooltip: {
                y: {
                    formatter: (val: number) =>
                        this.formatValue(report, this.normalizeNumber(val)),
                },
            },
        };
    }

    private createLineOptions(report: DynamicDashboardReport): any {
        const data = report.data;
        const usesPercentageSeries = report.chartType === "LINE_PERCENT";
        const seriesData = usesPercentageSeries
            ? data.series
            : data.totalValues?.length
            ? data.totalValues
            : data.series;

        return {
            series: [
                {
                    name: report.name,
                    data: this.normalizeNumberArray(seriesData),
                },
            ],
            colors: this.getChartColors(1),
            chart: {
                type: "area",
                stacked: false,
                height: this.getAdaptiveChartHeight(report, 350),
                width: "100%",
                redrawOnParentResize: true,
                redrawOnWindowResize: true,
                zoom: {
                    type: "x",
                    enabled: true,
                    autoScaleYaxis: true,
                },
                toolbar: {
                    autoSelected: "zoom",
                },
            },
            dataLabels: {
                enabled: false,
            },
            markers: {
                size: 4,
            },
            fill: {
                type: "gradient",
                gradient: {
                    shadeIntensity: 1,
                    inverseColors: false,
                    opacityFrom: 0.5,
                    opacityTo: 0,
                    stops: [0, 90, 100],
                },
            },
            stroke: {
                curve: "smooth",
                width: 3,
            },
            yaxis: {
                labels: {
                    formatter: (val: number) =>
                        usesPercentageSeries
                            ? `${this.normalizeNumber(val).toFixed(0)}%`
                            : this.formatValue(report, this.normalizeNumber(val)),
                },
            },
            xaxis: {
                categories: data.categories ?? [],
            },
            tooltip: {
                shared: false,
                y: {
                    formatter: (val: number, opts: any) => {
                        const index = opts.dataPointIndex;

                        if (report.chartType === "LINE_PERCENT") {
                            const absences = data.totalAbsences?.[index] ?? 0;
                            const total = data.totalAppointments?.[index] ?? 0;
                            return `${this.normalizeNumber(val).toFixed(2)}% - ${absences} de ${total}`;
                        }

                        if (!usesPercentageSeries) {
                            return this.formatValue(
                                report,
                                this.normalizeNumber(val),
                            );
                        }

                        const totalValue = data.totalValues?.[index] ?? 0;
                        return `${this.normalizeNumber(val).toFixed(2)}% - ${this.formatValue(report, totalValue)}`;
                    },
                },
            },
        };
    }

    private createLineMultipleOptions(report: DynamicDashboardReport): any {
        const data = report.data || {};
        const encodedMultiSeries = this.isEncodedMultiSeriesReport(report)
            ? this.buildEncodedMultiSeries(report)
            : null;
        const categories = encodedMultiSeries
            ? encodedMultiSeries.categories
            : this.normalizeChartCategories(data.categories);
        const isGenericMultiSeries =
            report.chartType === "LINE_MULTI_SERIES_VALUE" ||
            report.chartType === "COLUMN_MULTIPLE_VALUE";
        const isColumnMultiple = report.chartType === "COLUMN_MULTIPLE_VALUE";
        const allowNegativeYAxis =
            isColumnMultiple ||
            report.code === "dashboards-financial-cash-flow-next-30-days";
        const series = encodedMultiSeries
            ? encodedMultiSeries.series
            : this.isNamedSeriesArray(data.series)
            ? data.series
                  .map((serie, index) => ({
                      name: `${serie?.name || `Serie ${index + 1}`}`,
                      data: this.normalizeChartSeriesData(serie?.data, categories.length),
                  }))
                  .filter((serie) => serie.data.length > 0)
            : [
                  {
                      name: this.getLineMultipleSeriesName(report, 0),
                      data: this.normalizeChartSeriesData(
                          data.appointmentsSeries,
                          categories.length,
                      ),
                  },
                  {
                      name: this.getLineMultipleSeriesName(report, 1),
                      data: this.normalizeChartSeriesData(
                          data.absencesSeries,
                          categories.length,
                      ),
                  },
              ];

        return {
            series: this.hasChartSeriesData(series) ? series : [],
            colors: this.getChartColors(series.length),
            chart: {
                type: isColumnMultiple
                    ? "bar"
                    : isGenericMultiSeries
                    ? "line"
                    : "area",
                stacked: false,
                height: this.getAdaptiveChartHeight(report, 350),
                width: "100%",
                redrawOnParentResize: true,
                redrawOnWindowResize: true,
            },
            plotOptions: isColumnMultiple
                ? {
                      bar: {
                          horizontal: false,
                          columnWidth: "58%",
                          borderRadius: 3,
                          borderRadiusApplication: "end",
                      },
                  }
                : undefined,
            dataLabels: {
                enabled: false,
            },
            markers: {
                size: 4,
            },
            fill: isColumnMultiple
                ? {
                      type: "solid",
                      opacity: 0.9,
                  }
                : isGenericMultiSeries
                ? {
                      type: "solid",
                      opacity: 1,
                  }
                : {
                      type: "gradient",
                      gradient: {
                          opacityFrom: 0.5,
                          opacityTo: 0,
                          stops: [0, 90, 100],
                      },
                  },
            stroke: {
                curve: isColumnMultiple ? "straight" : "smooth",
                width: isColumnMultiple ? 0 : 3,
            },
            yaxis: {
                min: allowNegativeYAxis ? undefined : 0,
                forceNiceScale: true,
                labels: {
                    formatter: (val: number) =>
                        this.isCurrencyLineMultiple(report)
                            ? this.formatValue(report, this.normalizeNumber(val))
                            : this.isDurationMinutesReport(report)
                            ? this.formatMinutes(val)
                            : this.normalizeNumber(val).toFixed(0),
                },
            },
            xaxis: {
                categories,
            },
            tooltip: {
                shared: true,
                intersect: false,
                y: {
                    formatter: (val: number) =>
                        this.isCurrencyLineMultiple(report)
                            ? this.formatValue(report, this.normalizeNumber(val))
                            : this.isDurationMinutesReport(report)
                            ? this.formatMinutes(val)
                            : new Intl.NumberFormat("pt-BR").format(
                                  this.normalizeNumber(val),
                              ),
                },
            },
            legend: {
                position: "top",
                horizontalAlign: "center",
                fontSize: "11px",
                itemMargin: {
                    horizontal: 8,
                    vertical: 2,
                },
            },
        };
    }

    private createPolarAreaOptions(report: DynamicDashboardReport): any {
        return {
            series: this.normalizeNumberArray(report.data.series),
            labels: report.data.labels ?? [],
            colors: this.getChartColors(report.data.labels?.length),
            chart: {
                type: "pie",
                height: this.getAdaptiveChartHeight(report, 360),
                width: "100%",
                redrawOnParentResize: true,
                redrawOnWindowResize: true,
            },
            stroke: {
                colors: ["#fff"],
                width: 1,
            },
            dataLabels: {
                enabled: true,
                formatter: (_val: number, opts: any) => {
                    const seriesValue =
                        opts?.w?.config?.series?.[opts.seriesIndex] ?? 0;

                    return this.formatValue(report, seriesValue);
                },
                style: {
                    fontSize: "11px",
                    fontWeight: 600,
                },
            },
            legend: {
                position: "bottom",
                fontSize: "11px",
                itemMargin: {
                    horizontal: 8,
                    vertical: 2,
                },
            },
            tooltip: {
                y: {
                    formatter: (val: number) => this.formatValue(report, val),
                },
            },
        };
    }

    private createRadialBarOptions(data: any): any {
        return {
            series: [this.normalizeNumber(data.value)],
            colors: this.getChartColors(1),
            chart: {
                height: this.getAdaptiveGroupedChartHeight(350),
                type: "radialBar",
                width: "100%",
                redrawOnParentResize: true,
                redrawOnWindowResize: true,
            },
            plotOptions: {
                radialBar: {
                    startAngle: -135,
                    endAngle: 225,
                    hollow: {
                        margin: 0,
                        size: "70%",
                    },
                    dataLabels: {
                        name: {
                            offsetY: -10,
                            show: true,
                        },
                        value: {
                            formatter: (val: number) =>
                                `${this.normalizeNumber(val).toFixed(2)}%`,
                            fontSize: "36px",
                            show: true,
                        },
                    },
                },
            },
            stroke: {
                lineCap: "round",
            },
            labels: [data.label || "Percentual"],
        };
    }

    private formatValue(report: DynamicDashboardReport, value: number): string {
        const normalizedValue = this.normalizeNumber(value);

        if (report.valueType === "BRL") {
            return new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
            }).format(normalizedValue);
        }

        return new Intl.NumberFormat("pt-BR").format(normalizedValue);
    }
}
